import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';
import apiClient from '../services/apiClient';
import { clearAllListQueries } from '../utils/listQueryStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Gibt die effektiven Rollen des eingeloggten Benutzers zurück.
   * Backwards-kompatibel: Falls effectiveRoles nicht im User-Objekt vorhanden
   * ist (Legacy-Token), wird ein leeres Array zurückgegeben.
   * @returns {string[]}
   */
  const effectiveRoles = useMemo(
    () => (user?.effectiveRoles && Array.isArray(user.effectiveRoles) ? user.effectiveRoles : []),
    [user]
  );

  /**
   * True wenn der Benutzer ausschließlich die Rolle RENTER hat und kein
   * anderes Recht besitzt. Legacy-User ohne effectiveRoles gelten nicht als RENTER.
   * @returns {boolean}
   */
  const isRenterOnly = useMemo(
    () => effectiveRoles.length > 0 && effectiveRoles.every(r => r === 'RENTER'),
    [effectiveRoles]
  );

  /**
   * True wenn für den eingeloggten Benutzer der Office-Export (DOCX/ODT/CSV/XLSX)
   * freigeschaltet ist. Legacy-User bzw. fehlendes Feld gelten als nicht freigeschaltet.
   * @returns {boolean}
   */
  const officeExportEnabled = useMemo(
    () => user?.officeExportEnabled === true,
    [user]
  );

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = useCallback(async (identifier, password, tenantSlug) => {
    const data = await authService.login(identifier, password, tenantSlug);
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (userData, tenantSlug) => {
    const data = await authService.register(userData, tenantSlug);
    // Registration starts an unauthenticated state (no JWT issued).
    // Reflect that: only setUser if the server exceptionally returned a user object.
    setUser(data.user || null);
    return data;
  }, []);

  const exchangeOAuthCode = useCallback(async (oneTimeCode, codeVerifier) => {
    const data = await authService.exchangeOAuthCode(oneTimeCode, codeVerifier);
    setUser(data.user);
    return data;
  }, []);

  const linkAccount = useCallback(async (linkingToken, password) => {
    const data = await authService.linkAccount(linkingToken, password);
    setUser(data.user);
    return data;
  }, []);

  const linkAccountWithOAuth = useCallback(async (linkingToken, reAuthCode, reAuthProvider, reAuthCodeVerifier) => {
    const data = await authService.linkAccountWithOAuth(linkingToken, reAuthCode, reAuthProvider, reAuthCodeVerifier);
    setUser(data.user);
    return data;
  }, []);

  const resetPassword = useCallback(async (token, newPassword, tenantSlug) => {
    const data = await authService.resetPassword(token, newPassword, tenantSlug);
    if (data.user) {
      setUser(data.user);
    }
    return data;
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    authService.updateStoredUser(updatedUser);
  }, []);

  /**
   * Refresht die Benutzerdaten einmalig beim Mount gegen GET /users/me, damit
   * server-seitig geänderte Flags (z. B. officeExportEnabled) auch dann
   * sichtbar werden, wenn der eingeloggte Benutzer sich nicht neu anmeldet.
   * Läuft nur, wenn bereits ein Token + User aus dem Storage geladen wurden;
   * Fehler (401, offline, ...) werden bewusst still ignoriert — ein 401 wird
   * bereits vom apiClient-Response-Interceptor global behandelt (Logout +
   * Redirect). AbortController verhindert einen setState nach Unmount bzw.
   * nach einem abgebrochenen Request (React StrictMode Doppel-Invoke).
   */
  useEffect(() => {
    const token = authService.getToken();
    const currentUser = authService.getCurrentUser();
    if (!token || !currentUser) {
      return undefined;
    }

    const controller = new AbortController();

    apiClient.get('/users/me', { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        // Der AuthProvider bleibt über Logout/Re-Login hinweg gemountet: Eine
        // verspätete Antwort der alten Session darf den neueren Zustand nicht
        // überschreiben, daher nur anwenden, wenn das Token noch dasselbe ist.
        if (authService.getToken() !== token) {
          return;
        }
        // Defensiv mergen statt ersetzen: /users/me trägt nicht zwingend alle
        // Felder der Login-Antwort (z. B. authType fehlt in manchen E2E-Stubs,
        // obwohl Produktiv-Backend dasselbe UserDTO liefert). Ein Vollersatz
        // würde solche Felder aus dem Storage verlieren. Basis ist der aktuell
        // im Storage liegende User (nicht der beim Effekt-Start gelesene
        // currentUser), damit ein zwischenzeitliches updateUser() an anderer
        // Stelle nicht überschrieben wird.
        updateUser({ ...authService.getCurrentUser(), ...response.data });
      })
      .catch(() => {
        // Bewusst ignoriert: 401 (Interceptor übernimmt), Offline, abgebrochener
        // Request. Der bisherige State bleibt unverändert erhalten.
      });

    return () => controller.abort();
  }, [updateUser]);

  const updateToken = useCallback((newToken) => {
    authService.updateStoredToken(newToken);
  }, []);

  const logout = useCallback(() => {
    // Gespeicherte Listenzustände (Filter/Sortierung/Seite) werden NUR beim ausdrücklichen Abmelden
    // (inkl. Kontolöschung) gelöscht. Bewusst nicht in authService.clearAllUserCaches (läuft auch beim
    // Login) und nicht im 401-Interceptor (Sitzungsablauf soll die Filter erhalten). try/finally: ein Fehler
    // in authService.logout darf weder das Löschen noch das Zurücksetzen des Benutzers verhindern. Das Löschen
    // erhöht zugleich die Epoche in listQueryStorage, sodass noch laufende Anfragen nichts mehr schreiben.
    try {
      authService.logout();
    } finally {
      clearAllListQueries();
      setUser(null);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    login,
    register,
    exchangeOAuthCode,
    linkAccount,
    linkAccountWithOAuth,
    resetPassword,
    updateUser,
    updateToken,
    logout,
    loading,
    effectiveRoles,
    isRenterOnly,
    officeExportEnabled
  }), [user, login, register, exchangeOAuthCode, linkAccount, linkAccountWithOAuth,
       resetPassword, updateUser, updateToken, logout, loading, effectiveRoles, isRenterOnly,
       officeExportEnabled]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Wie useAuth(), wirft aber NICHT, wenn kein AuthProvider im Baum ist —
 * gibt stattdessen `null` zurück. Für Komponenten, die optional von einem
 * eingeloggten Kontext profitieren sollen, aber auch außerhalb eines
 * AuthProvider gerendert werden müssen (z. B. ContractWizard.js in
 * bestehenden Jest-Suiten ohne AuthProvider-Wrapper).
 * @returns {object|null}
 */
export const useAuthOptional = () => useContext(AuthContext);

/**
 * Liefert die effektiven Rollen des eingeloggten Benutzers.
 * Gibt ein leeres Array zurück wenn kein User eingeloggt ist oder
 * der Token keine effectiveRoles enthält (Backwards-Compatibility).
 * @returns {string[]}
 */
export const useEffectiveRoles = () => {
  const { effectiveRoles } = useAuth();
  return effectiveRoles;
};

/**
 * Gibt true zurück wenn der eingeloggte Benutzer ausschließlich die
 * Rolle RENTER besitzt. Legacy-User ohne effectiveRoles-Feld gelten
 * nicht als reine Mieter.
 * @returns {boolean}
 */
export const useIsRenterOnly = () => {
  const { isRenterOnly } = useAuth();
  return isRenterOnly;
};

/**
 * Gibt true zurück wenn für den eingeloggten Benutzer der Office-Export
 * (DOCX/ODT/CSV/XLSX) freigeschaltet ist. Legacy-User bzw. fehlendes Feld
 * gelten als nicht freigeschaltet.
 * @returns {boolean}
 */
export const useOfficeExportEnabled = () => useAuth().officeExportEnabled;

export default AuthContext;
